import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import TestimonialCarousel from '../components/TestimonialCarousel';
import { Button } from '../components/ui/button';
import {
  Sparkles, ArrowRight, Check, MessageSquare, Compass, Map, TrendingUp,
  Rocket, Award, Palette, Layers, Users as UsersIcon, Briefcase, Building2,
  ChevronRight, PhoneCall, CalendarCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const processSteps = [
  { icon: PhoneCall, num: 1, title: 'FREE 20-Minute Jumpstart Session', desc: 'Quick clarity call to understand your business goals, current stage, and biggest challenges.' },
  { icon: MessageSquare, num: 2, title: '1-on-1 Business Strategy Session', subTitle: '$197', desc: 'A personalized 60-minute strategy session focused on business structure, launch strategy, branding, credibility, systems, or growth planning.' },
  { icon: Map, num: 3, title: 'Custom Growth Roadmap', desc: 'Receive actionable next steps, tools, resources, and strategic direction tailored to your goals.' },
  { icon: TrendingUp, num: 4, title: 'Ongoing Coaching & Growth Programs', desc: 'Continue with accountability, implementation support, and long-term business growth guidance.' },
];

const programCards = [
  {
    id: 'free-jumpstart', highlight: true, badge: 'Start Here',
    title: 'FREE Business Jumpstart Session', price: 'FREE', duration: '20 Minutes',
    description: 'A free introductory clarity session designed to help aspiring entrepreneurs identify next steps, overcome obstacles, and gain direction for launching or growing their business.',
    features: ['Business idea discussion', 'Startup direction', 'Goal clarity', 'Q&A session', 'Personalized recommendations'],
    cta: 'Book Free Session', href: '/free-jumpstart-session', testId: 'card-free-jumpstart',
  },
  {
    id: 'strategy-session', accent: 'blue',
    title: '1-on-1 Business Strategy Session', price: '$197', duration: '60 Minutes',
    description: 'A personalized strategy session focused on helping entrepreneurs launch, structure, or grow their business with clear action steps and expert guidance.',
    features: ['Personalized business strategy', 'Launch roadmap', 'Business structure guidance', 'Branding direction', 'Credibility recommendations', 'Action plan after session'],
    cta: 'View Details', href: '/business-strategy-session', testId: 'card-strategy-session',
  },
  {
    id: 'accelerator',
    title: 'Business Jumpstart Accelerator', price: '$997', payment: 'or 3 payments of $397', duration: '3 Months',
    description: 'An accountability-based coaching program designed to help entrepreneurs move from idea to launch with structure, consistency, and support.',
    features: ['Bi-weekly coaching sessions', 'Launch strategy support', 'Business credibility setup', 'Branding & positioning guidance', 'Accountability check-ins', 'Resource vault access', 'Email/message support'],
    cta: 'View Details', href: '/business-jumpstart-accelerator', testId: 'card-accelerator',
  },
  {
    id: 'partnership', accent: 'premium',
    title: 'Business Growth Partnership', price: '$1,997', payment: 'or monthly payment option', duration: '6 Months',
    description: 'A long-term business development partnership focused on implementation, consistency, systems, and sustainable growth.',
    features: ['Ongoing coaching support', 'Monthly growth planning', 'Systems & organization guidance', 'Business structure refinement', 'Marketing & positioning support', 'Accountability coaching', 'Priority support access'],
    cta: 'View Details', href: '/business-growth-partnership', testId: 'card-partnership',
  },
  {
    id: 'mentorship', accent: 'premium',
    title: 'Visionary CEO Mentorship', price: '$4,997', payment: 'or monthly payment option', duration: '12 Months',
    description: 'A high-level mentorship experience for serious entrepreneurs committed to long-term growth, leadership, scalability, and business expansion.',
    features: ['Long-term strategy partnership', 'Leadership development', 'Business scaling guidance', 'Multiple income stream planning', 'Systems implementation', 'Advanced accountability', 'Priority consulting access'],
    cta: 'View Details', href: '/visionary-mentorship', testId: 'card-mentorship',
  },
];

const servicesConnection = [
  { icon: Rocket, title: 'Business Jumpstart Strategy', desc: 'Launch with a clear structure and a tactical 90-day plan.' },
  { icon: Award, title: 'Business Credibility Setup', desc: 'LLC, EIN, business credit, and the credibility stack.' },
  { icon: Palette, title: 'Brand Foundation & Presence', desc: 'Brand voice, visuals, and online presence that converts.' },
  { icon: Layers, title: 'Business Systems & Organization', desc: 'CRM, automations, and operational backbone.' },
  { icon: UsersIcon, title: 'One-on-One Consulting', desc: 'Direct personalized guidance for your unique challenges.' },
  { icon: Briefcase, title: 'Training & Workshops', desc: 'Group learning experiences and live training sessions.' },
];

const idealClients = [
  'Aspiring entrepreneurs', 'Side hustlers', 'Working professionals',
  'Coaches & consultants', 'Small business owners', 'Ministry & nonprofit founders',
  'Entrepreneurs needing structure', 'People ready to create additional income streams',
];

function ProgramCard({ p, processing, onApply }) {
  const accent = p.accent || 'default';
  const isHero = p.highlight;
  const cardClass = isHero
    ? 'bg-gradient-to-br from-[#0F172A] to-[#1E1B4B] text-white border-2 border-[#F97316] shadow-2xl shadow-[#1D4ED8]/20'
    : accent === 'premium'
    ? 'bg-white border-2 border-[#1D4ED8]/30 shadow-xl shadow-slate-100'
    : accent === 'blue'
    ? 'bg-white border border-[#1D4ED8]/20 shadow-lg shadow-slate-100'
    : 'bg-white border border-[#E2E8F0] shadow-md shadow-slate-100';
  const priceColor = isHero ? 'text-[#FB923C]' : accent === 'premium' ? 'text-[#1D4ED8]' : 'text-[#0A0F1C]';
  const headingColor = isHero ? 'text-white' : 'text-[#0A0F1C]';
  const descColor = isHero ? 'text-[#94A3B8]' : 'text-[#475569]';
  const checkColor = isHero ? 'text-[#FB923C]' : accent === 'premium' ? 'text-[#1D4ED8]' : 'text-[#10B981]';

  return (
    <div className={`relative rounded-2xl p-7 transition-all hover:-translate-y-1 flex flex-col ${cardClass}`} data-testid={p.testId}>
      {p.badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F97316] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
          {p.badge}
        </span>
      )}
      <div className="mb-4">
        <h3 className={`text-xl font-bold ${headingColor} mb-2 leading-tight`}>{p.title}</h3>
        <div className="flex items-baseline gap-2 mb-1">
          <span className={`text-3xl font-bold ${priceColor}`}>{p.price}</span>
          <span className={`text-sm ${descColor}`}>· {p.duration}</span>
        </div>
        {p.payment && <div className={`text-xs ${descColor}`}>{p.payment}</div>}
      </div>
      <p className={`text-sm ${descColor} mb-5 leading-relaxed`}>{p.description}</p>
      <ul className="space-y-2 mb-6 flex-1">
        {p.features.map((f, i) => (
          <li key={i} className={`flex items-start gap-2 text-sm ${isHero ? 'text-[#E2E8F0]' : 'text-[#475569]'}`}>
            <Check size={14} className={`${checkColor} mt-0.5 flex-shrink-0`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {p.href ? (
        <Link to={p.href} className={`btn-primary-orange inline-flex items-center justify-center gap-2 w-full ${isHero ? '' : ''}`} data-testid={`${p.testId}-cta`}>
          {p.cta} <ArrowRight size={14} />
        </Link>
      ) : (
        <Button
          onClick={() => onApply(p)}
          disabled={processing === p.id}
          className={accent === 'premium' ? 'btn-primary-blue w-full' : 'btn-primary-orange w-full'}
          data-testid={`${p.testId}-cta`}
        >
          {processing === p.id ? 'Loading...' : (<><span>{p.cta}</span><ArrowRight size={14} className="ml-2" /></>)}
        </Button>
      )}
    </div>
  );
}

export default function ProgramsPage() {
  const [processing] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const handleProgramCTA = () => {}; // all cards now use href - kept for ProgramCard signature

  useEffect(() => {
    axios.get(`${API_URL}/api/testimonials`)
      .then((r) => setTestimonials(r.data.filter((t) => t.is_featured)))
      .catch(() => {});
  }, []);

  return (
    <Layout
      title="Coaching Programs - DCA Enterprises"
      description="Launch, structure and grow your business with expert 1-on-1 consulting and coaching programs from David C. Ashley."
    >
      <div className="bg-[#FAFAFA] min-h-screen" data-testid="programs-page">
        {/* SECTION 1 - HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white py-16 sm:py-24">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
          <div className="absolute -top-10 -left-10 w-80 h-80 rounded-full bg-[#1D4ED8] opacity-30 blur-3xl"></div>
          <div className="absolute -bottom-10 -right-10 w-80 h-80 rounded-full bg-[#F97316] opacity-20 blur-3xl"></div>

          <div className="container-custom relative z-10 text-center max-w-4xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-xs sm:text-sm font-semibold rounded-full mb-5" data-testid="programs-badge">
              <Sparkles size={14} className="text-[#FB923C]" /> Premium Business Consulting
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight" data-testid="programs-title">
              Launch, Structure & Grow Your Business With{' '}
              <span className="bg-gradient-to-r from-[#60A5FA] to-[#F97316] bg-clip-text text-transparent">Expert Guidance</span>
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] mb-8 leading-relaxed max-w-3xl mx-auto">
              Helping aspiring entrepreneurs and working professionals build legitimate businesses, create additional income streams, and launch with clarity, structure, and confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/schedule" className="btn-primary-orange inline-flex items-center justify-center gap-2 text-base sm:text-lg px-6 py-3" data-testid="hero-primary-cta">
                Book Your FREE 20-Minute Business Jumpstart Session <ArrowRight size={16} />
              </Link>
              <a href="#programs" className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white hover:bg-white/20 backdrop-blur-sm font-bold text-base px-6 py-3 rounded-lg transition-colors" data-testid="hero-secondary-cta">
                Explore Coaching Programs
              </a>
            </div>
          </div>
        </section>

        {/* SECTION 2 - HOW THE PROCESS WORKS */}
        <section className="container-custom py-14 sm:py-20" data-testid="process-section">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <div className="text-xs font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">The Path Forward</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0F1C] mb-3">How the Process Works</h2>
            <p className="text-[#475569]">From a free intro call to long-term partnership, every step is designed to move you forward with clarity.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {processSteps.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.num} className="relative bg-white border border-[#E2E8F0] rounded-2xl p-6 hover:shadow-lg transition-shadow" data-testid={`process-step-${s.num}`}>
                  <div className="absolute -top-3 left-6 bg-[#1D4ED8] text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-md">{s.num}</div>
                  <div className="w-11 h-11 rounded-lg bg-[#EFF6FF] flex items-center justify-center mb-3 mt-2">
                    <Icon size={20} className="text-[#1D4ED8]" />
                  </div>
                  <h3 className="font-bold text-[#0A0F1C] text-base mb-1 leading-snug">{s.title}</h3>
                  {s.subTitle && <div className="text-xs font-semibold text-[#F97316] mb-2">{s.subTitle}</div>}
                  <p className="text-sm text-[#475569] leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* SECTION 3 - FEATURED PROGRAMS */}
        <section id="programs" className="bg-white py-14 sm:py-20" data-testid="programs-grid-section">
          <div className="container-custom">
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <div className="text-xs font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">Coaching Programs</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0F1C] mb-3">Choose Your Level of Support</h2>
              <p className="text-[#475569]">Start with a free clarity call, drop into a one-time strategy session, or partner with David long-term.</p>
            </div>
            {/* Hero free card spans top, then grid below */}
            <div className="grid lg:grid-cols-12 gap-5 mb-5">
              <div className="lg:col-span-12">
                <ProgramCard p={programCards[0]} processing={processing} onApply={handleProgramCTA} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {programCards.slice(1).map((p) => (
                <ProgramCard key={p.id} p={p} processing={processing} onApply={handleProgramCTA} />
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4 - SERVICES CONNECTION */}
        <section className="container-custom py-14 sm:py-20" data-testid="services-connection">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <div className="text-xs font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">The Toolkit</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0F1C] mb-3">What We Help Entrepreneurs Build</h2>
            <p className="text-[#475569]">The coaching programs above help you implement these services.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {servicesConnection.map((s, i) => {
              const Icon = s.icon;
              return (
                <Link to="/services" key={i} className="group bg-white border border-[#E2E8F0] rounded-xl p-5 hover:shadow-lg hover:border-[#1D4ED8] transition-all flex items-start gap-4" data-testid={`svc-conn-${i}`}>
                  <div className="w-11 h-11 rounded-lg bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#1D4ED8]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#0A0F1C] text-base mb-1">{s.title}</h3>
                    <p className="text-sm text-[#475569] mb-2">{s.desc}</p>
                    <div className="text-xs font-semibold text-[#1D4ED8] flex items-center gap-1 group-hover:gap-2 transition-all">Learn More <ChevronRight size={12} /></div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* SECTION 5 - IDEAL CLIENT */}
        <section className="bg-white py-14 sm:py-20" data-testid="ideal-client-section">
          <div className="container-custom max-w-4xl">
            <div className="text-center mb-10">
              <div className="text-xs font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">Who This Is For</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0F1C] mb-3">Built for Entrepreneurs Ready to Move</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {idealClients.map((c, i) => (
                <div key={i} className="flex items-center gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3" data-testid={`ideal-${i}`}>
                  <div className="w-7 h-7 rounded-full bg-[#1D4ED8] text-white flex items-center justify-center flex-shrink-0">
                    <Check size={14} />
                  </div>
                  <span className="text-sm font-medium text-[#0A0F1C]">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 6 - TESTIMONIALS CAROUSEL */}
        <section className="py-14 sm:py-20 bg-[#FAFAFA]" data-testid="testimonials-section">
          <div className="container-custom">
            <div className="text-center mb-10 max-w-2xl mx-auto">
              <div className="text-xs font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">Social Proof</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0F1C] mb-3">Client Success Stories</h2>
              <p className="text-[#475569]">Real entrepreneurs across every coaching program sharing real transformations.</p>
            </div>
          </div>
          <TestimonialCarousel testimonials={testimonials} bgGradientFrom="#FAFAFA" />
        </section>

        {/* SECTION 7 - FINAL CTA */}
        <section className="bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white py-16 sm:py-20" data-testid="final-cta-section">
          <div className="container-custom text-center max-w-3xl mx-auto relative">
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-20 h-20 rounded-full bg-[#F97316] opacity-20 blur-3xl"></div>
            <CalendarCheck size={40} className="text-[#FB923C] mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight">Your Business Vision Deserves Structure, Strategy & Support</h2>
            <p className="text-base sm:text-lg text-[#94A3B8] mb-7">Book your FREE 20-minute Business Jumpstart Session and take the next step toward launching or growing your business.</p>
            <Link to="/schedule" className="btn-primary-orange inline-flex items-center gap-2 text-base sm:text-lg px-7 py-3" data-testid="final-cta-btn">
              Book Free Session <ArrowRight size={16} />
            </Link>
          </div>
        </section>

        {/* STICKY MOBILE CTA - only visible on small screens */}
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur border-t border-[#E2E8F0] shadow-2xl shadow-slate-900/10 px-4 py-3" data-testid="sticky-mobile-cta">
          <Link to="/schedule" className="btn-primary-orange w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3" data-testid="sticky-mobile-cta-btn">
            <CalendarCheck size={16} /> Book FREE 20-Min Jumpstart <ArrowRight size={14} />
          </Link>
        </div>
        <div className="lg:hidden h-20" aria-hidden="true"></div>
      </div>
    </Layout>
  );
}
